import React from 'react';

import PropTypes               from 'prop-types';
import MaterialReactTable      from 'material-react-table';
import Alert                   from 'components/Alert';
import Typography              from '@mui/material/Typography';
import { MRT_Localization_PL } from 'material-react-table/locales/pl';
import { useTranslation }      from 'react-i18next';
import useSWRInfinite          from 'swr/infinite';
import createRestRequestConfig from 'rest/createRestRequestConfig';
import { AuthContext }         from 'App';
import useStyles               from './infiniteScrollTableStyles';
import classnames              from 'classnames';
import { OTHER_CONFIG }        from 'rest/const';

/**
 * @param {ResponseType} res
 * @returns res?.data
 */
export const DEFAULT_GET_DATA_FROM_RESPONSE = (res) => res?.data;


export default function InfiniteScrollTable({
    getDataFromResponse = DEFAULT_GET_DATA_FROM_RESPONSE,
    pathFormat, queryParams, tableTL, alert, setAlert, ...otherProps
}) {

  const { classes }      = useStyles();
  const { t: defaultTL } = useTranslation();
  const { userInfo }     = React.useContext(AuthContext);

  const t = React.useMemo(() => {
    if (typeof tableTL !== 'function') {
      return (key, options) => defaultTL(key, options);
    }

    return (key, options) => {
      const result = tableTL(key, options);
      if (result !== key) {
        return result;
      }
      return defaultTL(key, options);
    }
  }, [tableTL, defaultTL]);

  const [fetchError,    setFetchError]    = React.useState(null);
  const [scheduleReset, setScheduleReset] = React.useState(false);

  const defaultFetchSize   = OTHER_CONFIG.fetchSize;
  const fetchSize          = otherProps.fetchSize ?? defaultFetchSize;  //Nullish coalescing operator
  const paginationDisabled = fetchSize === 0;


  function getLengthFromResponse(response) {
    return getDataFromResponse(response)?.length;
  }

  function getKey(pageIndex, previousResponse) {
    if (!paginationDisabled && previousResponse && getLengthFromResponse(previousResponse) < fetchSize)
      return null; // reached the end

    const axiosCfg = createRestRequestConfig(userInfo.accessToken, true);
    axiosCfg.params = queryParams;

    const pageNumber = pageIndex + 1; // Indexes start from 0 while gateway API starts from 1
    const path = pathFormat
      .replace('{page}', pageNumber)
      .replace('{per_page}', '' + fetchSize);

    return [path, 'GET', null, axiosCfg];
  }

  const swrOptions = {
    ...otherProps.swrOptions,
    onError: (error) => {
      if (typeof otherProps.swrOptions?.onError === 'function') {
        otherProps.swrOptions.onError(error);
      }
      setFetchError(error);
      setScheduleReset(false);
    },
    onSuccess: (res) => {
      if (typeof otherProps.swrOptions?.onSuccess === 'function') {
        otherProps.swrOptions.onSuccess(res);
      }
      setFetchError(null);
      setScheduleReset(false);
    },
  };

  const { data: responseArray, isLoading, isValidating, setSize, mutate } = useSWRInfinite(getKey, swrOptions);

  //useSWRInfinite wraps whatever fetcher returns in an array, therefore it's an array of responses
  const data    = responseArray ? responseArray.flatMap(getDataFromResponse) : [];
  const isEmpty = getLengthFromResponse(responseArray?.[0]) === 0;

  function isEndReached() {
    if (isEmpty || paginationDisabled)
      return true;
    if (!responseArray)
      return false;

    const lastResponse   = responseArray[responseArray.length - 1];
    const lastDataLength = getLengthFromResponse(lastResponse);
    return lastDataLength < fetchSize;
  }

  const endReached = isEndReached();

  //Called on scroll and possibly on mount to fetch more data as the user scrolls and reaches bottom of table
  const fetchMoreOnBottomReached = React.useCallback(
    (containerRefElement) => {
      if (scheduleReset || isLoading || isValidating || endReached) {
        return;
      }
      const { scrollHeight, scrollTop, clientHeight } = containerRefElement;
      //Once the user has scrolled within 100px of the bottom of the table, fetch more data if we can
      const fetchAtDistanceFromBottom = 100;
      if (scrollHeight - scrollTop - clientHeight < fetchAtDistanceFromBottom) {
        setAlert?.(null);
        setFetchError(null);
        //Scroll up, this way it won't do multiple fetches at once
        containerRefElement.scrollTop = scrollTop - fetchAtDistanceFromBottom + 5;
        setSize(prevSize => prevSize + 1);
      }
    }, [scheduleReset, isLoading, isValidating, endReached, setSize, setAlert]
  );

  /**
   * General props
   * translation, columns and data
   */

  function fetchStatusText() {
    if (fetchError) {
      return t('fetchError');
    }
    if (scheduleReset || isLoading || isValidating) {
      return t('isFetching');
    }
    if (endReached) {
      return t('finishedFetching').replace('{totalFetched}', '' + data.length);
    }
    return t('currentlyFetched').replace('{totalFetched}', '' + data.length);
  }

  const localization = React.useMemo(
    () => {
      const localization = t('localization', { returnObjects: true });
      return { ...MRT_Localization_PL, ...localization };
    }, [t]
  );

  const [previousQueryParams, setPreviousQueryParams] = React.useState(null);

  const resettingTableRef   = React.useRef(undefined);
  resettingTableRef.current = { previousQueryParams, mutate };

  /**
   * Schedules resetting table data when query params change.
   */
  React.useEffect(() => {
    const _previousQueryParams = resettingTableRef.current.previousQueryParams;
    const _mutate = resettingTableRef.current.mutate;
    if (_previousQueryParams != null) {
      setScheduleReset(true);
      if (JSON.stringify(_previousQueryParams) === JSON.stringify(queryParams)) {
        _mutate(); // It doesn't automatically fetch stuff if contents of params don't change
      }
    }
    setPreviousQueryParams(queryParams || null); // Don't set undefined
  }, [queryParams]);

  //false positive warning
  // noinspection RequiredAttributes
  return (
    <MaterialReactTable
      localization={localization}
      enableStickyHeader={true}
      enableStickyFooter={true}
      enableTopToolbar={typeof otherProps.renderTopToolbar === 'function'}
      enablePagination={false}
      enableSorting={false}
      enableColumnActions={false}
      renderBottomToolbarCustomActions={() => alert
        ? <Alert
            alert={alert}
            setAlert={setAlert}
            classes={{
              alertContainer: classes.alertContainer,
              paper:          classes.alertPaper,
            }}
          />
        : <Typography className={classes.fetchingToolbar}>
            {fetchStatusText()}
          </Typography>
      }

      {...otherProps}

      data={scheduleReset ? [] : data}
      muiBottomToolbarProps={{
        ...otherProps.muiBottomToolbarProps,
        className: classnames(classes.paginationStyles, otherProps.muiBottomToolbarProps?.className),
      }}
      muiTableContainerProps={{
        ...otherProps.muiTableContainerProps,
        //Half of the screen, so it doesn't generate scroll
        sx: {
          maxHeight: '50vh',
          ...otherProps.muiTableContainerProps?.sx,
        },
        //Add an event listener to the table container element
        onScroll: (event) => {
          if (otherProps.muiTableContainerProps?.onScroll)
            otherProps.muiTableContainerProps.onScroll(event);
          fetchMoreOnBottomReached(event.target);
        },
      }}
      state={{
        ...otherProps.state,
        isLoading:        scheduleReset || isLoading || (isValidating && data.length === 0),
        showProgressBars: scheduleReset || isLoading || isValidating,
      }}
    />
  );
}

InfiniteScrollTable.propTypes = {
  ...MaterialReactTable.propTypes,
  /**
   * <pre>
   * Columns returned by 'prepareColumns' function
   * </pre>
   */
  columns: PropTypes.arrayOf(PropTypes.object).isRequired,
  /**
   * <pre>
   * An address to fetch data from, optionally with placeholders for page index and fetch size per page.
   * The pagination placeholders are as follows: '{page}', '{per_page}'.
   * </pre>
   */
  pathFormat: PropTypes.string.isRequired,
  /**
   * <pre>
   * A function which is provided with axios response object and is supposed to extract an array with data from it.
   * Useful when the data to be displayed is just a part of the response.
   *
   * @see DEFAULT_GET_DATA_FROM_RESPONSE
   * </pre>
   */
  getDataFromResponse: PropTypes.func,
  /**
   * <pre>
   * Additional query params of each request.
   * The table will clear each time they change.
   * 'page' and 'per_page' query params are usually passed in pathFormat.
   * Filters are usually passed as queryParams instead of being in pathFormat.
   * </pre>
   */
  queryParams: PropTypes.object,
  /**
   * <pre>
   * How many records to fetch per each GET request.
   * It will also be used to replace '{per_page}' placeholder in pathFormat.
   * Endpoints might limit records per page and that will be the max fetched even if fetchSize is bigger.
   *
   *                                             WARNING
   * If endpoint doesn't support pagination then set it to 0, otherwise it WILL fetch endlessly.
   *
   * @see DEFAULT_FETCH_SIZE
   * </pre>
   */
  fetchSize: PropTypes.number,
  alert: PropTypes.object,
  setAlert: PropTypes.func,
  swrOptions: PropTypes.object,
};
